import cn from "classnames";
import React from "react";
import Badge from "web-ui/components/internal/Badge";
import Tag from "web-ui/components/internal/Tag";
import { buttonKeyDownHandler } from "web-ui/util/a11y";

import type { VoiceListVoice } from "../../api/generalTts";

import styles from "./VoiceCard.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  onSelect?: () => void;
  state?: "enabled" | "selected";
  tabIndex?: number;
  unavailable?: boolean;
  voice: VoiceListVoice;
}

const VoiceCard = ({
  onSelect,
  state = "enabled",
  tabIndex = 0,
  voice,
}: Props) => {
  // TODO: add quality information
  const quality = undefined as "high" | "acceptable" | "low" | undefined;
  const tags = [];
  if (voice.character !== undefined) {
    tags.push(formatCharacter(voice.character));
  }
  if (voice.gender !== undefined) {
    tags.push(titleCaseFormatter(voice.gender));
  }
  if (voice.age !== undefined) {
    tags.push(titleCaseFormatter(voice.age));
  }
  return (
    <div
      aria-selected={state === "selected"}
      className={cn(styles.card, {
        [styles.selected]: state === "selected",
        [styles.unavailable]: voice.status === "UNAVAILABLE",
      })}
      onClick={() => onSelect?.()}
      onKeyDown={buttonKeyDownHandler(() => onSelect?.())}
      tabIndex={tabIndex}
    >
      <div className={styles.header}>
        <span className={styles.name}>{voice.alias ?? voice.name}</span>
        {quality === "high" ? (
          <Badge className={styles.badge} color="green" size="s">
            High quality
          </Badge>
        ) : quality === "acceptable" ? (
          <Badge className={styles.badge} color="yellow" size="s">
            Acceptable
          </Badge>
        ) : quality === "low" ? (
          <Badge className={styles.badge} color="red" size="s">
            Low quality
          </Badge>
        ) : null}
      </div>
      <div className={styles.footer}>
        <span className={styles.meta}>
          {formatLanguage(
            ...(voice.languageStatuses.length
              ? voice.languageStatuses.map(status => status.language)
              : [voice.languageId]),
          )}
        </span>
        <span className={styles.pipe} />
        <span className={styles.meta}>{formatProvider(voice.provider)}</span>
        {tags.length ? (
          <>
            <span className={styles.pipe} />
            <span className={styles.tags}>
              {tags.map(tag => (
                <Tag isGhost={true} key={tag} size="s">
                  {tag}
                </Tag>
              ))}
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default VoiceCard;
